/**
 * coachImage.js — canonical image resolver for every listing surface.
 *
 * Same rules apply to browse cards, listing detail, seller dashboard,
 * admin data-management rows, and auction linked-listing views.
 *
 *   pickPhoto(listing)   → single best URL
 *   pickGallery(listing) → cleaned array of URLs (never empty)
 *
 * Rules:
 *   1. Prefer verified featured_image.
 *   2. Fall back to first valid entry in photos / images / media / gallery.
 *   3. Support legacy aliases (photo_urls, media_urls, image_url).
 *   4. Reject null / blank / example.test / customer-assets / blob: /
 *      broken relative paths.
 *   5. Vanturis-owned placeholder when no valid image exists.
 *   6. Never returns "" or undefined — always yields a renderable URL.
 */

const PLACEHOLDER = "/assets/coach-photos/_placeholder.svg";

const BAD_HOST_PATTERNS = [
  /example\.test/i,
  /customer-assets/i,
  /^blob:/i,
  /^data:/i,   // reject inline blobs — we want static assets
];

function isValid(src) {
  if (!src || typeof src !== "string") return false;
  const s = src.trim();
  if (!s) return false;
  for (const re of BAD_HOST_PATTERNS) if (re.test(s)) return false;
  // absolute URL or root-relative path
  return s.startsWith("/") || /^https?:\/\//i.test(s);
}

function extract(item) {
  if (!item) return null;
  if (typeof item === "string") return item;
  if (typeof item === "object") return item.url || item.src || item.href || null;
  return null;
}

export function pickPhoto(listing) {
  if (!listing) return PLACEHOLDER;
  // 1. verified featured image
  const featured = extract(listing.featured_image) || extract(listing.cover_image)
                    || extract(listing.hero_image) || extract(listing.hero_photo_url);
  if (isValid(featured)) return featured;
  // 2. arrays (photos > images > media > gallery > legacy)
  for (const key of ["photos", "images", "media", "gallery",
                      "photo_urls", "media_urls"]) {
    const arr = listing[key];
    if (Array.isArray(arr)) {
      for (const item of arr) {
        const src = extract(item);
        if (isValid(src)) return src;
      }
    }
  }
  // 3. single-string legacy
  const single = extract(listing.image_url) || extract(listing.thumbnail);
  if (isValid(single)) return single;
  return PLACEHOLDER;
}

export function pickGallery(listing) {
  if (!listing) return [PLACEHOLDER];
  const out = [];
  const seen = new Set();
  const push = (v) => {
    if (isValid(v) && !seen.has(v)) { seen.add(v); out.push(v); }
  };
  const featured = extract(listing.featured_image) || extract(listing.cover_image)
                    || extract(listing.hero_image) || extract(listing.hero_photo_url);
  push(featured);
  for (const key of ["photos", "images", "media", "gallery",
                      "photo_urls", "media_urls"]) {
    const arr = listing[key];
    if (Array.isArray(arr)) arr.forEach((it) => push(extract(it)));
  }
  return out.length ? out : [PLACEHOLDER];
}

export const COACH_PLACEHOLDER = PLACEHOLDER;
